/**
 * A rehype plugin that turns real waterbody-overview links (see
 * `navigation.ts`'s `buildWaterbodyUrl`/`parseWaterbodyUrl`) into real
 * `<fish-citation-waterbody>` chip elements, as part of the
 * markdown-to-HTML pipeline itself -- see `@onxmaps/ai-toolkit`'s
 * `markdownOptions`/`rehypePlugins` hook (`MarkdownRendererOptions`), wired
 * in via `AiDependencies.markdownOptions` (`control-ai-chat.vue`) and this
 * module's `buildFishMarkdownOptions`.
 *
 * Because this runs synchronously inside the render pipeline, a citation is
 * born fully-formed in the HTML string `<ai-toolkit-markdown>` ever
 * produces -- there is no "raw, unhydrated citation" state to flash or hide,
 * unlike an earlier version of this mechanism that patched already-rendered
 * DOM after the fact.
 *
 * Display data comes from an in-memory cache built by scanning the
 * conversation's own `queryWaterbodies` tool-result parts' `structuredContent`
 * -- never a second network round-trip, and read fresh on every call (this
 * plugin is created once and reused for the component's whole lifetime, so
 * it must never close over a stale snapshot). A citation the cache doesn't
 * have (e.g. an old conversation whose tool-result message has scrolled out)
 * falls back to the link's own text -- the model writes a real link with
 * real text. If there's neither a cache hit nor link text, the anchor is
 * left alone -- since its href is a real, absolute onX URL rather than an
 * inert scheme, the toolkit's own external-link handling still treats it as
 * a normal (if unstyled) link, opening in a new tab rather than navigating
 * away from the app.
 *
 * When a citation's only container is a `<p>` (the shape a "loose" Markdown
 * list item renders, per CommonMark), the whole `<p>` is replaced with the
 * chip rather than just the anchor -- the chip already carries the same
 * name/distance/potential data the model tends to restate as prose right
 * next to it. Only done when the `<p>` has exactly one citation, so a
 * paragraph that happens to reference multiple lakes doesn't lose all but
 * one chip.
 */

import { visit } from 'unist-util-visit'
import type { Plugin } from 'unified'
import type { Root, Element, ElementContent } from 'hast'
import type {
  ConversationStore,
  MessageUser,
  PartToolResult,
} from '@onxmaps/ai-toolkit'
import type { MarkdownOptions } from '@onxmaps/ai-toolkit/components/ai-toolkit-markdown'
import { NAME as QUERY_WATERBODIES_NAME } from '@/fish/lib/ai/tools/tool-query-waterbodies'
import { TAG_NAME as WATERBODY_TAG_NAME } from '@/fish/lib/ai/components/citation-waterbody'
import { parseWaterbodyUrl } from '@/fish/lib/ai/navigation'

interface WaterbodyCitationContent {
  name: string
  distanceText: string | null
  direction: string | null
  potentialMatches: { category: string; label: string; species: string[] }[]
}

interface RawWaterbodyResult {
  id: string
  name: string
  distanceText: string | null
  direction: string | null
  potentialMatches: WaterbodyCitationContent['potentialMatches']
}

const buildWaterbodyCache = (
  convo: ConversationStore
): Map<string, WaterbodyCitationContent> => {
  const cache = new Map<string, WaterbodyCitationContent>()
  for (const message of convo.getMessages()) {
    if (message.role !== 'user') {
      continue
    }
    for (const part of (message as MessageUser).content) {
      if (part.type !== 'tool_result') {
        continue
      }
      const toolResult = part as PartToolResult
      if (toolResult.name !== QUERY_WATERBODIES_NAME) {
        continue
      }
      const results = (
        toolResult.structuredContent as
          | { results?: RawWaterbodyResult[] }
          | undefined
      )?.results
      results?.forEach((result) => {
        cache.set(result.id, {
          name: result.name,
          distanceText: result.distanceText,
          direction: result.direction,
          potentialMatches: result.potentialMatches,
        })
      })
    }
  }
  return cache
}

const isElement = (node: ElementContent): node is Element =>
  node.type === 'element'

const extractText = (node: Element): string =>
  node.children
    .map((child) => {
      if (child.type === 'text') {
        return child.value
      }
      return isElement(child) ? extractText(child) : ''
    })
    .join('')

/** The waterbody id when `node` is an anchor linking to its overview page. */
const waterbodyCitationId = (node: ElementContent): string | null => {
  if (!isElement(node) || node.tagName !== 'a') {
    return null
  }
  const href = node.properties.href
  return typeof href === 'string' ? parseWaterbodyUrl(href) : null
}

const fallbackContent = (name: string): WaterbodyCitationContent | null => {
  return name
    ? { name, distanceText: null, direction: null, potentialMatches: [] }
    : null
}

const buildChip = (
  anchor: Element,
  id: string,
  cache: Map<string, WaterbodyCitationContent>
): Element | null => {
  const content = cache.get(id) ?? fallbackContent(extractText(anchor))
  if (!content) {
    return null
  }

  const properties: Record<string, string> = {
    'data-id': id,
    'data-name': content.name,
    'data-potential': JSON.stringify(content.potentialMatches),
  }
  if (content.distanceText) {
    properties['data-distance-text'] = content.distanceText
  }
  if (content.direction) {
    properties['data-direction'] = content.direction
  }

  return {
    type: 'element',
    tagName: WATERBODY_TAG_NAME,
    properties,
    children: [],
  }
}

/** See this module's doc comment for the overall strategy. */
export function rehypeCitationWaterbody(
  getConvo: () => ConversationStore | undefined
): Plugin<[], Root> {
  return () => (tree: Root) => {
    const convo = getConvo()
    const cache = convo ? buildWaterbodyCache(convo) : new Map()

    visit(tree, 'element', (node, index, parent) => {
      if (node.tagName !== 'p' || index === undefined || !parent) {
        return
      }
      const citationAnchors = node.children.filter(
        (child) => waterbodyCitationId(child) !== null
      )
      if (citationAnchors.length !== 1) {
        return
      }
      const anchor = citationAnchors[0]
      const id = waterbodyCitationId(anchor)
      const chip = id && isElement(anchor) ? buildChip(anchor, id, cache) : null
      if (!chip) {
        return
      }
      parent.children[index] = chip
    })

    visit(tree, 'element', (node, index, parent) => {
      const id = waterbodyCitationId(node)
      if (!id || index === undefined || !parent) {
        return
      }
      const chip = buildChip(node, id, cache)
      if (!chip) {
        return
      }
      parent.children[index] = chip
    })
  }
}

/** Fish's `AiDependencies.markdownOptions` implementation -- see `app/lib/ai/index.ts`. */
export function buildFishMarkdownOptions(
  getConvo: () => ConversationStore | undefined
): MarkdownOptions {
  return { rehypePlugins: [rehypeCitationWaterbody(getConvo)] }
}
